import { useEffect, useRef } from 'react';
import { getVapidPublicKey, pushSubscribe } from '@/shared/services/pushService';

/**
 * Pede permissao de notificacao e cria/atualiza subscription no backend.
 * Idempotente: roda 1x por sessao por usuario; subscription nova se trocou kind.
 *
 * Uso:
 *   usePushSubscribe({ enabled: isGM, userNum: user?.userNum, kind: 1 });
 *
 * Pre-requisitos:
 *  - HTTPS (caballiberty.com)
 *  - /sw.js servido em /
 *  - PWA installable (manifest.json + icones) — Apple iOS so funciona apos
 *    "Adicionar a Tela de Inicio".
 */
export function usePushSubscribe(opts: {
  enabled: boolean;
  userNum?: number | null;
  charIdx?: number | null;
  kind: 0 | 1;                          // 0=player, 1=gm
}) {
  const ranRef = useRef(false);

  useEffect(() => {
    if (!opts.enabled) return;
    if (ranRef.current) return;
    ranRef.current = true;

    (async () => {
      try {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
          console.warn('[push] navegador nao suporta Service Worker / Push');
          return;
        }

        // Registrar SW se ainda nao tem
        const reg = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;

        // Pedir permissao
        let perm = Notification.permission;
        if (perm === 'default') perm = await Notification.requestPermission();
        if (perm !== 'granted') {
          console.info('[push] permissao negada/ignorada pelo usuario');
          return;
        }

        // VAPID public key do backend
        const { publicKey } = await getVapidPublicKey();

        // Se ja tem subscription, reusar se a applicationServerKey bate
        let sub = await reg.pushManager.getSubscription();
        if (sub) {
          // Compara base64 da appServerKey existente (best-effort)
          const existingKey = sub.options.applicationServerKey
            ? bufToBase64Url(sub.options.applicationServerKey as ArrayBuffer)
            : null;
          if (existingKey && existingKey !== publicKey) {
            await sub.unsubscribe();
            sub = null;
          }
        }

        if (!sub) {
          // Cast pra BufferSource — Uint8Array<ArrayBufferLike> nao bate
          // direto com BufferSource em TS estrito.
          const appServerKey = base64UrlToUint8Array(publicKey);
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: appServerKey.buffer as ArrayBuffer,
          });
        }

        await pushSubscribe({
          userNum: opts.userNum ?? null,
          charIdx: opts.charIdx ?? null,
          kind: opts.kind,
          userAgent: navigator.userAgent.slice(0, 256),
          subscription: sub.toJSON() as PushSubscriptionJSON,
        });

        console.info('[push] inscrito ok (kind=' + opts.kind + ')');
      } catch (err) {
        console.error('[push] falha:', err);
      }
    })();
  }, [opts.enabled, opts.userNum, opts.charIdx, opts.kind]);
}

// helpers VAPID base64url <-> bytes
function base64UrlToUint8Array(base64Url: string): Uint8Array {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

function bufToBase64Url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let str = '';
  for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
